import {Types} from "@Common/types";
import {cascadeSum} from "@Common/utils/cascade";
import ReactGA from "react-ga4";
export default ReactGA;

type Item            = Types.Apis.Models.Item;
type Cart            = Types.Apis.Models.Cart;
type TemplateOptions = Types.Apis.Models.TemplateOptions;

type Enumerate<N extends number, Acc extends number[] = []> = Acc['length'] extends N
  ? Acc[number]
  : Enumerate<N, [...Acc, Acc['length']]>

type Range<F extends number, T extends number> = Exclude<Enumerate<T>, Enumerate<F>> | T

export const addGAEvent = ({category, action, label, value}:{category:string, action:string, label:string, value:Range<1, 100>}) => {
    ReactGA.event({category, action, label, value});
};

export const setGAUserProperties = (userProperties) => {
    ReactGA.gtag("set", "user_properties", userProperties);
};

export const ReactGAGuestModal = (
    {type} :
    {type: "create"|"login"}
) => (
    ReactGA.gtag('event', 'guest_modal', {
        'type': type,
    })
);

export const ReactGAGuestEvent = (
    {eventName, statusCode="", reason="", method=""} :
    {eventName:string, statusCode?:string, reason?:string, method?:""|"username"|"phone"|"Google"|"Facebook"}
) => (
    ReactGA.gtag('event', 'guest_' + eventName, {
        ...(method && { 'method': method }),
        ...(reason && { 'reason': reason }),
        ...(statusCode && { 'statusCode': statusCode })
    })
);

export const ReactGAShare = (
    {method, contentType, idTemplate} :
    {method:"Twitter"|"Facebook"|"LinkedIn"|"Pinterest"|"Instagram", contentType:"image"|"text"|"link"|"video", idTemplate:number}
) => (
    ReactGA.gtag('event', 'share', {
        'method': method,
        'content_type': contentType,
        'item_id': idTemplate,
    })
);

// @deprecated Use sendGAError instead, it's convention to use lower-case for a procedural function
export const ReactGAError = ({errorCategory, errorMessage}) => (
    ReactGA.gtag('event', 'error', {
        'event_category': errorCategory,
        'event_label': errorMessage,
        'value': 1,
    })
);

export const sendGAError = ({errorCategory, errorMessage, ...params}) => (
    ReactGA.gtag('event', 'error', {
        'event_category': errorCategory,
        'event_label': errorMessage,
        'value': 1,
        ...params,
    })
);

export const ReactGATemplateEvent = ({eventName, templateOptions}:{eventName:string, templateOptions:TemplateOptions}) => {
    const purchase = {
        value: 0,
        currency: "USD",
        items: [{
            item_id: templateOptions.sku?.variation ?? templateOptions.sku.front,
            user_created_template: templateOptions.sku?.userCreatedTemplate ?? "",
            item_name: templateOptions.name,
            index: 1,
            item_category:  templateOptions?.revenueCategories?.idPrimary + "|" + templateOptions?.revenueCategories?.name,                //revenueCategory
            item_category2: templateOptions?.product?.idProduct + "|" + templateOptions?.product?.name,                                    //product
            item_category3: templateOptions?.categories?.frontTemplate?.id + "|" + templateOptions?.categories?.frontTemplate?.name,       //category
            item_category4: templateOptions?.subCategories?.frontTemplate?.id + "|" + templateOptions?.subCategories?.frontTemplate?.name, //subcategory
            item_category5: templateOptions?.styles?.frontTemplate?.id + "|" + templateOptions?.styles?.frontTemplate?.name,               //style
        }]
    };
    ReactGA.gtag("event", eventName, purchase);
}

export const ReactGASCEnrollment = ({eventName, idProgram, data=undefined}) => {
    // console.log("ReactGASCEnrollment", {eventName, idProgram, data});
    ReactGA.gtag("event", eventName, {
        type: "scheduled_campaigns",
        idProgram,
        ...data,
    });
};

export const ReactGAItemEvent = ({eventName, item}:{eventName:string, item:Item}) => {
    const purchase = {
        value: 0,
        currency: "USD",
        items: [mapItem(item, 1)]
    };
    ReactGA.gtag("event", eventName, purchase);
}

export const ReactGACartEvent = ({eventName, cart}:{eventName:string, cart:Cart}) => {
    const items = cart.items;
    const purchase = {
        transaction_id: cart.cartId,
        value: cascadeSum(items, "[*].itemTotals[*].amount", (amount,{},{2:p}) => (p.type !== "promoCode" ? 1 : -1) * amount),
        tax: cascadeSum(items, "[*].itemTotals[*]", (it) => it.type === "salesTax" ? it.amount : 0),
        shipping: cascadeSum(items, "[*].itemTotals[*]", (it) => it.type === "shippingCharge" ? it.amount : 0),
        currency: "USD",
        coupon: cart?.appliedCredits?.promoCodes?.[0]?.code ?? "",
        items: items.map((item, index) => mapItem(item, index))
    };
    ReactGA.gtag("event", eventName, purchase);
}

const mapItem = (item: Item, index) => ({
    item_id: item.sku?.variation ?? item.sku.front,
    user_created_template: item.sku?.userCreatedTemplate ?? "",
    order_id: item.itemId,
    distribution_type: item.selections.distributionType,
    item_name: item.metaData.templateName,
    discount: cascadeSum(item.itemTotals, "[*]", (it) => it.type === "promoCode" ? it.amount : 0) / (item.selections.quantity ?? 1),
    index: index + 1,
    item_category:  item?.metaData?.revenueCategories?.idPrimary + "|" + item?.metaData?.revenueCategories?.name,                //revenueCategory
    item_category2: item?.metaData?.product?.idProduct + "|" + item?.metaData?.product?.name,                                    //product
    item_category3: item?.metaData?.categories?.frontTemplate?.id + "|" + item?.metaData?.categories?.frontTemplate?.name,       //category
    item_category4: item?.metaData?.subCategories?.frontTemplate?.id + "|" + item?.metaData?.subCategories?.frontTemplate?.name, //subcategory
    item_category5: item?.metaData?.styles?.frontTemplate?.id + "|" + item?.metaData?.styles?.frontTemplate?.name,               //style
    price: cascadeSum(item.itemTotals, "[*]", (it) => it.type === "subTotal" ? it.amount : 0) / (item.selections.quantity ?? 1),
    quantity: item.selections.quantity ?? 1
})
